import type { DateRange } from '@/features/api/extranet/dto/shared'
import { addMonth, createUtcDate, getAge, getToday, isDate, toISODateOnlyString } from '@/features/shared/tools'
import { typesContratEligiblesMajoration10 } from './constantes'
import { withCache } from '@/features/shared/tools/function'
import { PropertyStatus } from '../../property-status'
import { AnneeFormation } from '../../types/opco/Demande/CAPP'
import { anneeChangementMinimumSmic, minimumRemunerationCap } from './constantes'
import type { RemunerationProperty } from './types'

export function getDureeContratMois(dateDebut: Date, dateFin: Date): number {
  let months = dateFin.getMonth() - dateDebut.getMonth() + 12 * (dateFin.getFullYear() - dateDebut.getFullYear())

  if (dateFin.getDate() < dateDebut.getDate()) months--

  return months
}

export function getRangeDateDebutExecutionContrat(dateConclusion: Date): DateRange {
  const dateMin = dateConclusion
  const dateMax = null

  const dateRange: DateRange = { start: toISODateOnlyString(dateMin), end: toISODateOnlyString(dateMax) }

  return dateRange
}

export function getRangeDateFinContratOuPeriodeApprentissage(dateDebutExecutionContrat: Date): DateRange {
  const dateMin: Date = dateDebutExecutionContrat
  const dateMax: Date = createUtcDate(
    dateDebutExecutionContrat.getFullYear() + 4,
    dateDebutExecutionContrat.getMonth(),
    dateDebutExecutionContrat.getDate(),
  )

  dateMax.setDate(dateMax.getDate() - 1)

  const dateRange: DateRange = { start: toISODateOnlyString(dateMin), end: toISODateOnlyString(dateMax) }

  return dateRange
}

export function getRangeDateDebutFormation(dateDebutContrat: Date | null): DateRange {
  let dateMax: Date | null = null
  if (dateDebutContrat) {
    dateMax = createUtcDate(
      dateDebutContrat.getUTCFullYear(),
      dateDebutContrat.getUTCMonth() + 3,
      dateDebutContrat.getUTCDate(),
    )
  }

  const dateRange: DateRange = { start: toISODateOnlyString(dateDebutContrat), end: toISODateOnlyString(dateMax) }

  return dateRange
}

export function getRangeDatePrevueFinEpreuvesOuExamens(dateDebutFormationCfa: Date): DateRange {
  const dateRange: DateRange = { start: toISODateOnlyString(dateDebutFormationCfa), end: null }

  return dateRange
}

export function getRangeDateConclusion(): DateRange {
  // Pour la Date min, j'ai pris une date min mais à redéfinir avec le métier
  const dateMin = createUtcDate(2018, 0, 1)
  const dateMax = getToday()

  const dateRange: DateRange = { start: toISODateOnlyString(dateMin), end: toISODateOnlyString(dateMax) }

  return dateRange
}

export function getRangeDateEffetAvenant(dateConclusion: Date): DateRange {
  const dateRange: DateRange = { start: toISODateOnlyString(dateConclusion), end: null }

  return dateRange
}

export function getRangeDateNaissanceMaitreApprentissage(dateConclusion: Date | null): DateRange {
  let dateMin: Date | null = null
  let dateMax: Date | null = null

  const maintenant: Date = getToday()
  if (dateConclusion !== null) {
    dateMax = createUtcDate(
      dateConclusion.getUTCFullYear() - 18,
      dateConclusion.getUTCMonth(),
      dateConclusion.getUTCDate(),
    )
    dateMin = createUtcDate(
      dateConclusion.getUTCFullYear() - 75,
      dateConclusion.getUTCMonth(),
      dateConclusion.getUTCDate(),
    )
  } else {
    dateMax = createUtcDate(maintenant.getUTCFullYear() - 18, maintenant.getUTCMonth(), maintenant.getUTCDate())
    dateMin = createUtcDate(maintenant.getUTCFullYear() - 75, maintenant.getUTCMonth(), maintenant.getUTCDate())
  }

  const dateRange: DateRange = { start: toISODateOnlyString(dateMin), end: toISODateOnlyString(dateMax) }

  console.log(dateRange)

  return dateRange
}

export function getRangeDateDebutFormationChezEmployeur(
  dateDebutExecutionContrat: Date,
  dateFinContratOuPeriodeApprentissage: Date | null = null,
): DateRange {
  const dateMin: Date | null = dateDebutExecutionContrat
  const dateMax: Date | null = dateFinContratOuPeriodeApprentissage

  const dateRange: DateRange = { start: toISODateOnlyString(dateMin), end: toISODateOnlyString(dateMax) }

  return dateRange
}

function getRemunerationPropertyBase(dateNaissance: Date, dateDebut: Date, dateFin: Date): Array<RemunerationProperty> {
  const remunerationProperty: Array<RemunerationProperty> = []

  if (dateNaissance && dateDebut && dateFin) {
    dateDebut = createUtcDate(dateDebut.getUTCFullYear(), dateDebut.getUTCMonth(), dateDebut.getUTCDate())
    dateFin = createUtcDate(dateFin.getUTCFullYear(), dateFin.getUTCMonth(), dateFin.getUTCDate())

    let nbAnnee: number = dateFin.getUTCFullYear() - dateDebut.getUTCFullYear()
    if (
      dateFin.getUTCMonth() > dateDebut.getUTCMonth() ||
      (dateFin.getUTCMonth() === dateDebut.getUTCMonth() && dateFin.getUTCDate() > dateDebut.getUTCDate())
    )
      nbAnnee++

    for (let annee = 0; annee < nbAnnee; annee++) {
      const dateDebutAnnee: Date = createUtcDate(
        dateDebut.getUTCFullYear() + annee,
        dateDebut.getUTCMonth(),
        dateDebut.getUTCDate(),
      )
      const dateFinAnnee: Date =
        annee === nbAnnee - 1
          ? dateFin
          : createUtcDate(dateDebut.getUTCFullYear() + annee + 1, dateDebut.getUTCMonth(), dateDebut.getUTCDate() - 1)

      const ageDebut = getAge(
        dateNaissance,
        createUtcDate(dateDebutAnnee.getUTCFullYear(), dateDebutAnnee.getUTCMonth(), dateDebutAnnee.getUTCDate()),
      )
      const ageFin = getAge(
        dateNaissance,
        createUtcDate(dateFinAnnee.getUTCFullYear(), dateFinAnnee.getUTCMonth(), dateFinAnnee.getUTCDate()),
      )
      const dateAnniversaireDebutAnnee: Date = createUtcDate(
        dateDebutAnnee.getUTCFullYear(),
        dateNaissance.getUTCMonth(),
        dateNaissance.getUTCDate(),
      )

      /*
      console.log(
        toISODateOnlyString(dateDebutAnnee),
        ageDebut,
        toISODateOnlyString(dateFinAnnee),
        ageFin,
        isPassage,
        toISODateOnlyString(dateAnniversaireDebutAnnee),
      )
        */

      const dateDebutAnneePeriode1: Date = createUtcDate(
        dateDebutAnnee.getUTCFullYear(),
        dateDebutAnnee.getUTCMonth(),
        dateDebutAnnee.getUTCDate(),
      )
      let dateFinAnneePeriode1: Date = createUtcDate(
        dateFinAnnee.getUTCFullYear(),
        dateFinAnnee.getUTCMonth(),
        dateFinAnnee.getUTCDate(),
      )
      let dateDebutAnneePeriode2: Date | null = null
      let dateFinAnneePeriode2: Date | null = null

      const isPassage =
        anneeChangementMinimumSmic.includes(ageFin) &&
        ageDebut !== ageFin &&
        dateAnniversaireDebutAnnee.getTime() !== dateDebutAnnee.getTime()

      /*
      console.log(
        toISODateOnlyString(dateDebutAnnee),
        ageDebut,
        toISODateOnlyString(dateFinAnnee),
        ageFin,
        isPassage,
        toISODateOnlyString(dateDebutAnneePeriode1),
        toISODateOnlyString(dateFinAnneePeriode1),
        toISODateOnlyString(dateFin),
        dateFinAnneePeriode1.getTime() < dateFin.getTime(),
        nbAnnee,
      )*/
      if (isPassage && (dateFinAnneePeriode1.getTime() <= dateFin.getTime() || nbAnnee === 1)) {
        const anneeNaissance =
          dateNaissance.getUTCMonth() < dateDebutAnnee.getUTCMonth()
            ? dateFinAnnee.getUTCFullYear()
            : dateDebutAnnee.getUTCFullYear()
        const dateAnniversaireAnnee: Date = createUtcDate(anneeNaissance, dateNaissance.getUTCMonth() + 1, 1)
        const dateFinAnneePeriode1Tmp = createUtcDate(
          dateAnniversaireAnnee.getUTCFullYear(),
          dateAnniversaireAnnee.getUTCMonth(),
          dateAnniversaireAnnee.getUTCDate() - 1,
        )
        const dateFinAnneePeriode2Tmp = createUtcDate(
          dateFinAnnee.getUTCFullYear(),
          dateFinAnnee.getUTCMonth(),
          dateFinAnnee.getUTCDate(),
        )
        if (dateAnniversaireAnnee.getTime() <= dateFinAnneePeriode2Tmp.getTime()) {
          dateFinAnneePeriode1 = dateFinAnneePeriode1Tmp
          dateDebutAnneePeriode2 = dateAnniversaireAnnee
          dateFinAnneePeriode2 = dateFinAnneePeriode2Tmp
        }
      }

      //console.log(annee, toISODateOnlyString(dateDebutAnnee), toISODateOnlyString(dateFinAnnee),ageDebut,ageFin, isPassage)
      const anneeFormation = AnneeFormation[annee]
      if (anneeFormation === undefined) continue

      remunerationProperty.push({
        annee: anneeFormation,
        minPourcentage: getMinimumPourcentage(ageDebut, annee + 1),
        status: PropertyStatus.Required,
        date: { start: toISODateOnlyString(dateDebutAnneePeriode1), end: toISODateOnlyString(dateFinAnneePeriode1) },
        age: isPassage ? ageDebut : ageFin,
      })

      remunerationProperty.push({
        annee: anneeFormation,
        minPourcentage: getMinimumPourcentage(ageFin, annee + 1),
        status: dateDebutAnneePeriode2 !== null ? PropertyStatus.Required : PropertyStatus.NotPresent,
        date: { start: toISODateOnlyString(dateDebutAnneePeriode2), end: toISODateOnlyString(dateFinAnneePeriode2) },
        age: ageFin,
      })
    }
  }

  return remunerationProperty
}
// Attention, ne fonctionne que si getRemunerationProperty est une fonction pure
// par exemple elle ne doit pas faire de Date.now()
export const getRemunerationProperty = withCache(getRemunerationPropertyBase, { cacheSize: 1 })

export function getMinimumPourcentage(age: number, annee: number): number {
  if (age < 18) {
    const retour = minimumRemunerationCap.Moins18
    if (annee === 1) {
      return retour.annee1
    } else if (annee === 2) {
      return retour.annee2
    } else {
      return retour.annee3
    }
  } else if (age <= 20) {
    const retour = minimumRemunerationCap.Moins21
    if (annee === 1) {
      return retour.annee1
    } else if (annee === 2) {
      return retour.annee2
    } else {
      return retour.annee3
    }
  } else if (age <= 25) {
    const retour = minimumRemunerationCap.Moins26
    if (annee === 1) {
      return retour.annee1
    } else if (annee === 2) {
      return retour.annee2
    } else {
      return retour.annee3
    }
  } else {
    const retour = minimumRemunerationCap.Plus25
    if (annee === 1) {
      return retour.annee1
    } else if (annee === 2) {
      return retour.annee2
    } else {
      return retour.annee3
    }
  }
}

export function isDateFinContratPlus2MoisFinFormation(
  stringDateFinContratOuPeriodeApprentissage: string | null,
  stringDatePrevueFinEpreuvesOuExamens: string | null,
): boolean {
  if (!isDate(stringDateFinContratOuPeriodeApprentissage) || !isDate(stringDatePrevueFinEpreuvesOuExamens)) return false

  const dateFinContratOuPeriodeApprentissage: Date = new Date(stringDateFinContratOuPeriodeApprentissage)
  const datePrevueFinEpreuvesOuExamens: Date = new Date(stringDatePrevueFinEpreuvesOuExamens)
  const dateFinContratOuPeriodeApprentissageMax: Date = addMonth(datePrevueFinEpreuvesOuExamens, 2)
  dateFinContratOuPeriodeApprentissageMax.setDate(dateFinContratOuPeriodeApprentissageMax.getDate() + 2)
  // console.log(dateFinContratOuPeriodeApprentissageMax)
  return dateFinContratOuPeriodeApprentissage > dateFinContratOuPeriodeApprentissageMax
}

export function getEstMajeurOuMineurEmancipe(options: {
  dateNaissance: string | null | undefined
  dateConclusion: string | null | undefined
  mineurEmancipe: boolean | null | undefined
}): boolean {
  const estMajeur =
    isDate(options.dateNaissance) &&
    isDate(options.dateConclusion) &&
    getAge(new Date(options.dateNaissance), new Date(options.dateConclusion)) >= 18
  if (estMajeur) return true

  return options.mineurEmancipe === true
}

export function getEstEligibleMajoration10(options: {
  typeContratAvenant: string | null | undefined
  dateDebutExecutionContrat: string | null | undefined
  dateFinContratOuPeriodeApprentissage: string | null | undefined
}): boolean {
  if (!options.typeContratAvenant || !typesContratEligiblesMajoration10.includes(options.typeContratAvenant)) {
    return false
  }

  if (!isDate(options.dateDebutExecutionContrat) || !isDate(options.dateFinContratOuPeriodeApprentissage)) {
    return false
  }

  const dateDebut = new Date(options.dateDebutExecutionContrat)
  const dateFin = new Date(options.dateFinContratOuPeriodeApprentissage)
  const dureeContratMois = getDureeContratMois(dateDebut, dateFin)

  return dureeContratMois < 12
}
